import { useLocation } from 'react-router-dom';
import React, { useEffect } from 'react';
import useHistory from '../../hooks/useHistory';
import { useUser } from '../../hooks/useUser';
import { observer } from 'mobx-react-lite';
import { fetchUnfinishedLoan, pushToDataLayer } from '../../utils';
import { withExpVariant } from '../../utils/kamExpVariant';
import { isProduction, loanPageviewEvents, LEND_API_ROUTE_PREFIX } from '../../constants';
import { isPrequalPath } from '../../utils/prequalRouteHelper';
import packageJson from '../../../package.json';
import useConfig from '../../stores/config';
import { useLoan } from '../../stores/store';
import { Spin } from 'antd';
import LendAPIRoutes from './LendAPIRoutes';
import TraditionalRoutes from './TraditionalRoutes';

const DYNAMIC_APP_INTERNAL_VERSION =
  process.env.REACT_APP_INTERNAL_VERSION || packageJson.version || '1.0.0';

type Props = {
  transitionsLoaded?: boolean;
};

const trackLoanPageView = (route: string) => {
  let key: string | undefined = 'type';
  const step = route.split('/').pop();
  key = step?.split('?')[0];
  const pageEvent = loanPageviewEvents.get(key);

  if (pageEvent) {
    pushToDataLayer(
      'pageview',
      withExpVariant({
        portal: 'trident funding',
        site_section: 'loan application',
        detailed_page_name: pageEvent,
        environment: isProduction() ? 'production' : 'qa',
        funnel: 'internal full-app',
      })
    );
  }
};

export default observer(function LoanIndex({ transitionsLoaded = false }: Props) {
  const history = useHistory();
  const user = useUser();
  const location = useLocation();
  const loanStore = useLoan();

  // VERSION IDENTIFIER - to confirm latest code is loaded (executes for all users entering full app)
  if ((window as any).__loanIndexVersionLogged !== true) {
    console.log(`Dynamic App Version: ${DYNAMIC_APP_INTERNAL_VERSION}`);
    (window as any).__loanIndexVersionLogged = true;
  }

  // Check if current route is LendAPI (no transitions needed for third-party iframe)
  // or the standalone Pre-Qual landing page — both bypass Firebase auth + config.
  const isLendAPIRoute =
    location.pathname.startsWith(LEND_API_ROUTE_PREFIX) || isPrequalPath(location.pathname);

  useEffect(() => {
    if (isLendAPIRoute) {
      localStorage.removeItem('loanId');
      return;
    }

    const unfinishedLoan = fetchUnfinishedLoan();
    const loanId = unfinishedLoan?.loanId;
    const currentStep = loanStore?.loan?.currentStep;
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');

    const refinedParams = new URLSearchParams(params.toString());
    refinedParams.delete('loanId');
    refinedParams.delete('currentStep');

    if (currentStep && loanId) {
      history.push(`/${loanId}/${currentStep}?${refinedParams}`);
    } else if (!source) {
      localStorage.removeItem('loanId');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanStore.loan, isLendAPIRoute]);

  useEffect(() => {
    if (!isLendAPIRoute) {
      trackLoanPageView(location.pathname);
    }
  }, [location, isLendAPIRoute]);

  // Track LendAPI Apply Now GA event - fires for all users entering full app funnel from LendAPI
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isFromLendAPI = params.get('lendAPI') === 'true';

    if (isFromLendAPI) {
      pushToDataLayer('link_click', {
        action_type: 'click',
        action_label: 'Apply now',
      });
    }
  }, []);

  // preload sources
  const config = useConfig();

  // LendAPI routes don't need Firebase Auth or Firestore config — render immediately
  if (isLendAPIRoute) {
    return <LendAPIRoutes />;
  }

  if (!user || !config) {
    return <Spin size="large" style={{ marginTop: '2rem', display: 'block' }} />;
  }

  return <TraditionalRoutes transitionsLoaded={transitionsLoaded} />;
});
